/**
 * SNES-style Audio Synthesizer Demo
 */

import { SnesSynth, FIR_PRESETS, GainMode } from '@/audio/SnesSynth';

export async function renderAudioDemo(appElement: HTMLDivElement): Promise<void> {
  const synth = new SnesSynth();

  appElement.className = 'scrollable-page';
  appElement.innerHTML = `
    <div style="padding: 20px; font-family: monospace; max-width: 800px;">
      <h2>SNES-style Synthesizer Demo</h2>
      <p>Click "Initialize Audio" first (required for browser audio policies).</p>

      <div style="margin: 20px 0; padding: 15px; background: #1a1a2e; border-radius: 8px;">
        <button id="init-btn" style="padding: 10px 20px; font-size: 16px; cursor: pointer;">
          Initialize Audio
        </button>
        <span id="status" style="margin-left: 15px; color: #888;">Not initialized</span>
      </div>

      <div id="controls" style="display: none;">
        <h3>Voice Controls</h3>
        <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px;">
          ${[0, 1, 2, 3, 4, 5, 6, 7]
            .map(
              (i) => `
            <div style="padding: 10px; background: #16213e; border-radius: 4px;">
              <div style="font-weight: bold; margin-bottom: 5px;">Voice ${i}</div>
              <select id="wave-${i}" style="width: 100%; margin-bottom: 5px;">
                <option value="sine">Sine</option>
                <option value="square">Square</option>
                <option value="saw">Saw</option>
                <option value="triangle">Triangle</option>
                <option value="noise">Noise</option>
              </select>
              <button class="play-btn" data-voice="${i}" style="width: 100%; padding: 5px;">
                Play
              </button>
            </div>
          `
            )
            .join('')}
        </div>

        <h3>Envelope Mode</h3>
        <div style="margin-bottom: 15px; padding: 10px; background: #16213e; border-radius: 8px;">
          <label style="margin-right: 20px;">
            <input type="radio" name="env-mode" value="adsr" id="env-mode-adsr" checked> ADSR Mode
          </label>
          <label>
            <input type="radio" name="env-mode" value="gain" id="env-mode-gain"> GAIN Mode
          </label>
        </div>

        <div id="adsr-controls">
          <h4>ADSR Controls</h4>
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 20px;">
            <div>
              <label>Attack (0-15)</label>
              <input type="range" id="attack" min="0" max="15" value="10" style="width: 100%;">
              <span id="attack-val">10</span>
            </div>
            <div>
              <label>Decay (0-7)</label>
              <input type="range" id="decay" min="0" max="7" value="5" style="width: 100%;">
              <span id="decay-val">5</span>
            </div>
            <div>
              <label>Sustain Level (0-7)</label>
              <input type="range" id="sustainLevel" min="0" max="7" value="5" style="width: 100%;">
              <span id="sustainLevel-val">5</span>
            </div>
            <div>
              <label>Sustain Decay (0-31)</label>
              <input type="range" id="sustainDecay" min="0" max="31" value="0" style="width: 100%;">
              <span id="sustainDecay-val">0</span>
            </div>
          </div>
        </div>

        <div id="gain-controls" style="display: none;">
          <h4>GAIN Controls</h4>
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 20px;">
            <div>
              <label>Gain Mode</label>
              <select id="gain-mode" style="width: 100%;">
                <option value="0">Direct (set level)</option>
                <option value="1">Linear Decrease</option>
                <option value="2">Exp Decrease</option>
                <option value="3">Linear Increase</option>
                <option value="4">Bent Increase</option>
              </select>
            </div>
            <div>
              <label>Gain Rate (0-31)</label>
              <input type="range" id="gain-rate" min="0" max="31" value="15" style="width: 100%;">
              <span id="gain-rate-val">15</span>
            </div>
            <div>
              <label>Gain Value (0-127)</label>
              <input type="range" id="gain-value" min="0" max="127" value="127" style="width: 100%;">
              <span id="gain-value-val">127</span>
            </div>
          </div>
          <p style="color: #888; font-size: 12px; margin-bottom: 15px;">
            Direct: Sets envelope to value*16. Others: Rate controls speed of increase/decrease.
          </p>
        </div>

        <h3>Echo / Reverb</h3>
        <div style="padding: 15px; background: #16213e; border-radius: 8px; margin-bottom: 20px;">
          <div style="margin-bottom: 10px;">
            <label>
              <input type="checkbox" id="echo-enabled"> Enable Echo
            </label>
          </div>
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px;">
            <div>
              <label>Delay (ms)</label>
              <input type="range" id="echo-delay" min="10" max="240" value="100" style="width: 100%;">
              <span id="echo-delay-val">100</span>
            </div>
            <div>
              <label>Feedback</label>
              <input type="range" id="echo-feedback" min="0" max="100" value="50" style="width: 100%;">
              <span id="echo-feedback-val">50</span>%
            </div>
            <div>
              <label>Volume</label>
              <input type="range" id="echo-volume" min="0" max="100" value="30" style="width: 100%;">
              <span id="echo-volume-val">30</span>%
            </div>
          </div>
          <div style="margin-top: 10px;">
            <label>FIR Preset:</label>
            <select id="fir-preset" style="margin-left: 10px;">
              <option value="flat">Flat</option>
              <option value="lowpass">Low-pass (Muffled)</option>
              <option value="echoDecay">Echo Decay</option>
              <option value="comb">Comb (Metallic)</option>
              <option value="highpass">High-pass</option>
              <option value="snesHall" selected>SNES Hall</option>
            </select>
          </div>
        </div>

        <h3>Keyboard</h3>
        <p style="color: #888; margin-bottom: 10px;">
          Use keys A-K for notes (white keys), W-U for sharps/flats (black keys)
        </p>
        <div id="keyboard" style="display: flex; gap: 2px; margin-bottom: 20px;">
          ${['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B', 'C']
            .map(
              (note, i) => `
            <button class="key-btn" data-semitone="${i}"
              style="padding: 30px 15px; min-width: 40px;
                     background: ${note.includes('#') ? '#333' : '#eee'};
                     color: ${note.includes('#') ? '#eee' : '#333'};">
              ${note}
            </button>
          `
            )
            .join('')}
        </div>

        <h3>Test Sequences</h3>
        <div style="display: flex; gap: 10px; flex-wrap: wrap;">
          <button id="test-arpeggio">Arpeggio</button>
          <button id="test-chord">Chord</button>
          <button id="test-bass">Bass Line</button>
          <button id="test-melody">Random Melody</button>
          <button id="test-all">All Voices</button>
        </div>

        <h3>Debug Oscilloscope</h3>
        <div style="padding: 15px; background: #16213e; border-radius: 8px;">
          <p style="color: #888; margin-bottom: 10px;">
            Click "Capture", play notes, then click "Stop" to see up to 5 seconds of audio.
          </p>
          <div style="display: flex; gap: 10px; margin-bottom: 10px;">
            <button id="capture-btn">Capture</button>
            <span id="capture-status" style="color: #888;"></span>
          </div>
          <canvas id="oscilloscope" width="760" height="200" style="background: #000; border: 1px solid #333; width: 100%; max-width: 760px;"></canvas>
          <div id="capture-info" style="color: #888; font-size: 12px; margin-top: 5px;"></div>

          <h4 style="margin-top: 15px;">Frequency Analysis</h4>
          <div id="freq-info" style="color: #0f0; font-size: 14px; margin-bottom: 10px; font-family: monospace;">
            Fundamental: -- Hz | Note: --
          </div>
          <canvas id="fft-canvas" width="760" height="150" style="background: #000; border: 1px solid #333; width: 100%; max-width: 760px;"></canvas>
          <div style="display: flex; justify-content: space-between; color: #666; font-size: 10px; max-width: 760px;">
            <span>0 Hz</span>
            <span>2 kHz</span>
            <span>4 kHz</span>
            <span>6 kHz</span>
            <span>8 kHz</span>
          </div>
        </div>
      </div>
    </div>
  `;

  const initBtn = document.getElementById('init-btn') as HTMLButtonElement;
  const statusEl = document.getElementById('status') as HTMLSpanElement;
  const controlsEl = document.getElementById('controls') as HTMLDivElement;

  initBtn.addEventListener('click', async () => {
    try {
      statusEl.textContent = 'Initializing...';
      await synth.init();
      await synth.resume();

      // Load default samples into all voices
      for (let i = 0; i < 8; i++) {
        const wave = (document.getElementById(`wave-${i}`) as HTMLSelectElement).value as
          | 'sine'
          | 'square'
          | 'saw'
          | 'triangle'
          | 'noise';
        synth.loadWaveform(i, wave);
        synth.setEnvelope(i, { attack: 10, decay: 5, sustainLevel: 5, sustainDecay: 0 });
      }

      statusEl.textContent = 'Ready!';
      statusEl.style.color = '#4ade80';
      controlsEl.style.display = 'block';
      initBtn.disabled = true;
    } catch (err) {
      statusEl.textContent = `Error: ${err}`;
      statusEl.style.color = '#f87171';
    }
  });

  // Wave selection
  for (let i = 0; i < 8; i++) {
    const select = document.getElementById(`wave-${i}`) as HTMLSelectElement;
    select.addEventListener('change', () => {
      if (!synth.isInitialized()) return;
      const wave = select.value as 'sine' | 'square' | 'saw' | 'triangle' | 'noise';
      synth.loadWaveform(i, wave);
    });
  }

  // Play buttons
  document.querySelectorAll('.play-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!synth.isInitialized()) return;
      const voice = parseInt((btn as HTMLButtonElement).dataset.voice!);
      synth.playNote(voice, 1.0, 0.5);
    });
  });

  // Envelope mode selection
  const envModeAdsr = document.getElementById('env-mode-adsr') as HTMLInputElement;
  const envModeGain = document.getElementById('env-mode-gain') as HTMLInputElement;
  const adsrControlsDiv = document.getElementById('adsr-controls') as HTMLDivElement;
  const gainControlsDiv = document.getElementById('gain-controls') as HTMLDivElement;

  let isAdsrMode = true;

  const updateEnvelopeMode = () => {
    isAdsrMode = envModeAdsr.checked;
    adsrControlsDiv.style.display = isAdsrMode ? 'block' : 'none';
    gainControlsDiv.style.display = isAdsrMode ? 'none' : 'block';

    if (!synth.isInitialized()) return;

    for (let i = 0; i < 8; i++) {
      synth.setEnvelope(i, { adsrEnabled: isAdsrMode });
    }
  };

  envModeAdsr.addEventListener('change', updateEnvelopeMode);
  envModeGain.addEventListener('change', updateEnvelopeMode);

  // ADSR controls
  ['attack', 'decay', 'sustainLevel', 'sustainDecay'].forEach((param) => {
    const input = document.getElementById(param) as HTMLInputElement;
    const valSpan = document.getElementById(`${param}-val`) as HTMLSpanElement;

    input.addEventListener('input', () => {
      valSpan.textContent = input.value;
      if (!synth.isInitialized()) return;

      for (let i = 0; i < 8; i++) {
        synth.setEnvelope(i, { [param]: parseInt(input.value) });
      }
    });
  });

  // GAIN mode controls
  const gainModeSelect = document.getElementById('gain-mode') as HTMLSelectElement;
  const gainRateInput = document.getElementById('gain-rate') as HTMLInputElement;
  const gainValueInput = document.getElementById('gain-value') as HTMLInputElement;

  const updateGainSettings = () => {
    if (!synth.isInitialized()) return;

    const gainMode = parseInt(gainModeSelect.value) as GainMode;
    const gainRate = parseInt(gainRateInput.value);
    const gainValue = parseInt(gainValueInput.value);

    for (let i = 0; i < 8; i++) {
      synth.setEnvelope(i, { gainMode, gainRate, gainValue });
    }
  };

  gainModeSelect.addEventListener('change', updateGainSettings);
  gainRateInput.addEventListener('input', () => {
    (document.getElementById('gain-rate-val') as HTMLSpanElement).textContent = gainRateInput.value;
    updateGainSettings();
  });
  gainValueInput.addEventListener('input', () => {
    (document.getElementById('gain-value-val') as HTMLSpanElement).textContent =
      gainValueInput.value;
    updateGainSettings();
  });

  // Echo controls
  const echoEnabled = document.getElementById('echo-enabled') as HTMLInputElement;
  const echoDelay = document.getElementById('echo-delay') as HTMLInputElement;
  const echoFeedback = document.getElementById('echo-feedback') as HTMLInputElement;
  const echoVolume = document.getElementById('echo-volume') as HTMLInputElement;
  const firPreset = document.getElementById('fir-preset') as HTMLSelectElement;

  const updateEcho = () => {
    if (!synth.isInitialized()) return;
    synth.setEcho({
      enabled: echoEnabled.checked,
      delay: parseInt(echoDelay.value),
      feedback: parseInt(echoFeedback.value) / 100,
      volumeL: parseInt(echoVolume.value) / 100,
      volumeR: parseInt(echoVolume.value) / 100,
      fir: FIR_PRESETS[firPreset.value as keyof typeof FIR_PRESETS],
    });
  };

  echoEnabled.addEventListener('change', updateEcho);
  echoDelay.addEventListener('input', () => {
    (document.getElementById('echo-delay-val') as HTMLSpanElement).textContent = echoDelay.value;
    updateEcho();
  });
  echoFeedback.addEventListener('input', () => {
    (document.getElementById('echo-feedback-val') as HTMLSpanElement).textContent =
      echoFeedback.value;
    updateEcho();
  });
  echoVolume.addEventListener('input', () => {
    (document.getElementById('echo-volume-val') as HTMLSpanElement).textContent = echoVolume.value;
    updateEcho();
  });
  firPreset.addEventListener('change', updateEcho);

  // Keyboard - alternates between voice 0 and 1 for overlapping notes
  let currentVoice = 0;
  let btnVoice = 0; // Track which voice the on-screen button is using

  // Base waveform is 256 samples at 32kHz = exactly 125Hz
  // To play a target frequency, pitch ratio = targetFreq / 125
  const BASE_FREQUENCY = 125;

  // Convert MIDI note number to pitch ratio for our 125Hz base waveform
  const midiToPitch = (midiNote: number): number => {
    const targetFreq = 440 * Math.pow(2, (midiNote - 69) / 12);
    return targetFreq / BASE_FREQUENCY;
  };

  document.querySelectorAll('.key-btn').forEach((btn) => {
    btn.addEventListener('mousedown', () => {
      if (!synth.isInitialized()) return;
      const semitone = parseInt((btn as HTMLButtonElement).dataset.semitone!);
      btnVoice = currentVoice;
      synth.noteOn(btnVoice, midiToPitch(60 + semitone));
      currentVoice = 1 - currentVoice; // Toggle between 0 and 1
    });
    btn.addEventListener('mouseup', () => {
      if (!synth.isInitialized()) return;
      synth.noteOff(btnVoice);
    });
    btn.addEventListener('mouseleave', () => {
      if (!synth.isInitialized()) return;
      synth.noteOff(btnVoice);
    });
  });

  // Computer keyboard input
  const keyMap: Record<string, number> = {
    a: 0,
    w: 1,
    s: 2,
    e: 3,
    d: 4,
    f: 5,
    t: 6,
    g: 7,
    y: 8,
    h: 9,
    u: 10,
    j: 11,
    k: 12,
  };

  // Track which voice each key is using
  const keyVoices = new Map<string, number>();

  document.addEventListener('keydown', (e) => {
    if (!synth.isInitialized()) return;
    if (e.repeat) return;

    const semitone = keyMap[e.key.toLowerCase()];
    if (semitone !== undefined && !keyVoices.has(e.key)) {
      const voice = currentVoice;
      keyVoices.set(e.key, voice);
      synth.noteOn(voice, midiToPitch(60 + semitone));
      currentVoice = 1 - currentVoice; // Toggle between 0 and 1
    }
  });

  document.addEventListener('keyup', (e) => {
    if (!synth.isInitialized()) return;

    const semitone = keyMap[e.key.toLowerCase()];
    if (semitone !== undefined && keyVoices.has(e.key)) {
      const voice = keyVoices.get(e.key)!;
      keyVoices.delete(e.key);
      synth.noteOff(voice);
    }
  });

  // Test sequences
  document.getElementById('test-arpeggio')?.addEventListener('click', () => {
    if (!synth.isInitialized()) return;

    const notes = [0, 4, 7, 12, 7, 4];
    notes.forEach((semitone, i) => {
      setTimeout(() => {
        synth.playNote(0, midiToPitch(60 + semitone), 0.15);
      }, i * 150);
    });
  });

  document.getElementById('test-chord')?.addEventListener('click', () => {
    if (!synth.isInitialized()) return;

    // Major chord across multiple voices
    [0, 4, 7, 12].forEach((semitone, i) => {
      synth.playNote(i, midiToPitch(60 + semitone), 1.0);
    });
  });

  document.getElementById('test-bass')?.addEventListener('click', () => {
    if (!synth.isInitialized()) return;

    // Load saw wave for bass (harmonics make low frequencies audible)
    synth.loadWaveform(7, 'saw');

    const bassLine = [0, 0, 5, 7, 5, 3, 0, -2];
    bassLine.forEach((semitone, i) => {
      setTimeout(() => {
        synth.playNote(7, midiToPitch(36 + semitone), 0.2);
      }, i * 250);
    });
  });

  document.getElementById('test-melody')?.addEventListener('click', () => {
    if (!synth.isInitialized()) return;

    // Pentatonic scale notes with variable rhythm
    const scale = [0, 2, 4, 7, 9, 12, 14, 16];
    const noteLengths = [150, 150, 300, 150, 300]; // Mix of 1/8 and 1/4 notes

    let time = 0;
    for (let i = 0; i < 16; i++) {
      const interval = noteLengths[Math.floor(Math.random() * noteLengths.length)];
      const duration = interval * 0.7; // 70% of interval, leaving 30% for release
      setTimeout(() => {
        const semitone = scale[Math.floor(Math.random() * scale.length)];
        synth.playNote(0, midiToPitch(60 + semitone), duration / 1000);
      }, time);
      time += interval;
    }
  });

  document.getElementById('test-all')?.addEventListener('click', () => {
    if (!synth.isInitialized()) return;

    // Play all 8 voices with different pitches
    for (let i = 0; i < 8; i++) {
      setTimeout(() => {
        synth.playNote(i, midiToPitch(48 + i * 2), 0.8);
      }, i * 100);
    }
  });

  // Oscilloscope capture
  const captureBtn = document.getElementById('capture-btn') as HTMLButtonElement;
  const captureStatus = document.getElementById('capture-status') as HTMLSpanElement;
  const captureInfo = document.getElementById('capture-info') as HTMLDivElement;
  const canvas = document.getElementById('oscilloscope') as HTMLCanvasElement;
  const ctx = canvas.getContext('2d')!;

  // FFT canvas
  const fftCanvas = document.getElementById('fft-canvas') as HTMLCanvasElement;
  const fftCtx = fftCanvas.getContext('2d')!;
  const freqInfo = document.getElementById('freq-info') as HTMLDivElement;

  let isCapturing = false;
  let capturedSamples: number[] = [];
  let capturedSampleRate = 32000; // DSP rate

  // Note names for frequency display
  const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

  const frequencyToNote = (freq: number): string => {
    if (freq <= 0) return '--';
    // A4 = 440Hz = MIDI 69
    const midiNote = 12 * Math.log2(freq / 440) + 69;
    const noteNum = Math.round(midiNote);
    const cents = Math.round((midiNote - noteNum) * 100);
    const octave = Math.floor(noteNum / 12) - 1;
    const noteName = NOTE_NAMES[noteNum % 12];
    const centsStr = cents >= 0 ? `+${cents}` : `${cents}`;
    return `${noteName}${octave} (${centsStr}¢)`;
  };

  // Autocorrelation-based pitch detection (more accurate than zero-crossing)
  const detectPitch = (samples: number[], sampleRate: number): number => {
    // Find a section with good signal
    let maxAmp = 0;
    let bestStart = 0;
    const windowSize = Math.min(4096, samples.length);

    for (let i = 0; i < samples.length - windowSize; i += windowSize / 4) {
      let amp = 0;
      for (let j = 0; j < windowSize; j++) {
        amp += Math.abs(samples[i + j]);
      }
      if (amp > maxAmp) {
        maxAmp = amp;
        bestStart = i;
      }
    }

    if (maxAmp < windowSize * 0.01) return 0; // Too quiet

    const data = samples.slice(bestStart, bestStart + windowSize);

    // Autocorrelation
    const minPeriod = Math.floor(sampleRate / 2000); // Max 2000 Hz
    const maxPeriod = Math.floor(sampleRate / 50); // Min 50 Hz

    let bestCorr = 0;
    let bestPeriod = 0;

    for (let period = minPeriod; period < maxPeriod && period < data.length / 2; period++) {
      let corr = 0;
      for (let i = 0; i < data.length - period; i++) {
        corr += data[i] * data[i + period];
      }
      corr /= data.length - period;

      if (corr > bestCorr) {
        bestCorr = corr;
        bestPeriod = period;
      }
    }

    if (bestPeriod === 0) return 0;
    return sampleRate / bestPeriod;
  };

  // Simple DFT for spectrum analysis (not full FFT but good enough for display)
  const computeSpectrum = (
    samples: number[],
    sampleRate: number,
    numBins: number
  ): Float32Array => {
    const spectrum = new Float32Array(numBins);
    const maxFreq = sampleRate / 2; // Nyquist
    const displayMaxFreq = 8000; // Only show up to 8kHz

    // Find a good section with signal
    let maxAmp = 0;
    let bestStart = 0;
    const windowSize = 2048;

    for (let i = 0; i < samples.length - windowSize; i += windowSize / 2) {
      let amp = 0;
      for (let j = 0; j < windowSize; j++) {
        amp += Math.abs(samples[i + j]);
      }
      if (amp > maxAmp) {
        maxAmp = amp;
        bestStart = i;
      }
    }

    const data = samples.slice(bestStart, bestStart + Math.min(windowSize, samples.length));

    // Apply Hann window
    const windowed = new Float32Array(data.length);
    for (let i = 0; i < data.length; i++) {
      const window = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (data.length - 1)));
      windowed[i] = data[i] * window;
    }

    // DFT for each frequency bin
    for (let bin = 0; bin < numBins; bin++) {
      const freq = (bin / numBins) * displayMaxFreq;
      const k = (freq / maxFreq) * (data.length / 2);

      let real = 0;
      let imag = 0;
      for (let n = 0; n < windowed.length; n++) {
        const angle = (2 * Math.PI * k * n) / windowed.length;
        real += windowed[n] * Math.cos(angle);
        imag -= windowed[n] * Math.sin(angle);
      }

      const magnitude = Math.sqrt(real * real + imag * imag) / windowed.length;
      // Convert to dB with floor
      spectrum[bin] = Math.max(-60, 20 * Math.log10(magnitude + 1e-10));
    }

    return spectrum;
  };

  const drawSpectrum = (spectrum: Float32Array) => {
    const width = fftCanvas.width;
    const height = fftCanvas.height;

    // Clear
    fftCtx.fillStyle = '#000';
    fftCtx.fillRect(0, 0, width, height);

    // Draw grid lines
    fftCtx.strokeStyle = '#222';
    fftCtx.beginPath();
    for (let i = 1; i < 4; i++) {
      const x = (i / 4) * width;
      fftCtx.moveTo(x, 0);
      fftCtx.lineTo(x, height);
    }
    for (let db = -60; db <= 0; db += 20) {
      const y = height * (1 - (db + 60) / 60);
      fftCtx.moveTo(0, y);
      fftCtx.lineTo(width, y);
    }
    fftCtx.stroke();

    // Draw spectrum
    fftCtx.strokeStyle = '#0f0';
    fftCtx.lineWidth = 1;
    fftCtx.beginPath();

    for (let i = 0; i < spectrum.length; i++) {
      const x = (i / spectrum.length) * width;
      const y = height * (1 - (spectrum[i] + 60) / 60);

      if (i === 0) {
        fftCtx.moveTo(x, y);
      } else {
        fftCtx.lineTo(x, y);
      }
    }
    fftCtx.stroke();

    // Fill under curve
    fftCtx.lineTo(width, height);
    fftCtx.lineTo(0, height);
    fftCtx.closePath();
    fftCtx.fillStyle = 'rgba(0, 255, 0, 0.1)';
    fftCtx.fill();
  };

  const analyzeFrequency = () => {
    if (capturedSamples.length === 0) {
      freqInfo.textContent = 'Fundamental: -- Hz | Note: --';
      fftCtx.fillStyle = '#000';
      fftCtx.fillRect(0, 0, fftCanvas.width, fftCanvas.height);
      return;
    }

    // Detect pitch
    const freq = detectPitch(capturedSamples, capturedSampleRate);
    const note = frequencyToNote(freq);
    freqInfo.textContent = `Fundamental: ${freq.toFixed(2)} Hz | Note: ${note}`;

    // Compute and draw spectrum
    const spectrum = computeSpectrum(capturedSamples, capturedSampleRate, fftCanvas.width);
    drawSpectrum(spectrum);
  };

  // Zoom and pan state
  let viewStart = 0; // Start sample index
  let viewEnd = 0; // End sample index
  let isDragging = false;
  let dragStartX = 0;
  let dragStartViewStart = 0;

  const drawWaveform = () => {
    const samples = capturedSamples;
    const sampleRate = capturedSampleRate;
    const width = canvas.width;
    const height = canvas.height;
    const midY = height / 2;

    // Clear canvas
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, width, height);

    // Draw center line
    ctx.strokeStyle = '#333';
    ctx.beginPath();
    ctx.moveTo(0, midY);
    ctx.lineTo(width, midY);
    ctx.stroke();

    if (samples.length === 0) return;

    // Calculate view range
    const viewSamples = viewEnd - viewStart;
    const samplesPerPixel = viewSamples / width;

    // Draw waveform
    ctx.strokeStyle = '#0f0';
    ctx.beginPath();

    for (let x = 0; x < width; x++) {
      const sampleIndex = Math.floor(viewStart + x * samplesPerPixel);
      const sample = samples[sampleIndex] || 0;
      const y = midY - sample * midY * 0.9;

      if (x === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();

    // Find and highlight discontinuities in view
    ctx.fillStyle = '#f00';
    let maxDiff = 0;
    let maxDiffIndex = 0;
    for (let i = Math.max(1, viewStart); i < viewEnd && i < samples.length; i++) {
      const diff = Math.abs(samples[i] - samples[i - 1]);
      if (diff > maxDiff) {
        maxDiff = diff;
        maxDiffIndex = i;
      }
      if (diff > 0.1) {
        const x = ((i - viewStart) / viewSamples) * width;
        ctx.fillRect(x - 1, 0, 2, height);
      }
    }

    // Show info
    const viewStartMs = (viewStart / sampleRate) * 1000;
    const viewEndMs = (viewEnd / sampleRate) * 1000;
    const totalMs = (samples.length / sampleRate) * 1000;
    captureInfo.textContent =
      `View: ${viewStartMs.toFixed(1)}-${viewEndMs.toFixed(1)}ms of ${totalMs.toFixed(1)}ms | ` +
      `Max discontinuity: ${maxDiff.toFixed(4)} at ${((maxDiffIndex / sampleRate) * 1000).toFixed(2)}ms | ` +
      `Scroll to zoom, drag to pan`;
  };

  const setViewRange = (start: number, end: number) => {
    viewStart = Math.max(0, Math.floor(start));
    viewEnd = Math.min(capturedSamples.length, Math.floor(end));
    if (viewEnd <= viewStart) viewEnd = viewStart + 100;
    drawWaveform();
  };

  const resetView = () => {
    viewStart = 0;
    viewEnd = capturedSamples.length;
    drawWaveform();
  };

  // Mouse wheel zoom
  canvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    if (capturedSamples.length === 0) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseRatio = mouseX / canvas.width;

    const viewSamples = viewEnd - viewStart;
    const zoomFactor = e.deltaY > 0 ? 1.2 : 0.8;
    const newViewSamples = Math.max(
      100,
      Math.min(capturedSamples.length, viewSamples * zoomFactor)
    );

    // Zoom centered on mouse position
    const mouseSample = viewStart + mouseRatio * viewSamples;
    const newStart = mouseSample - mouseRatio * newViewSamples;
    const newEnd = mouseSample + (1 - mouseRatio) * newViewSamples;

    setViewRange(newStart, newEnd);
  });

  // Mouse drag pan
  canvas.addEventListener('mousedown', (e) => {
    isDragging = true;
    dragStartX = e.clientX;
    dragStartViewStart = viewStart;
    canvas.style.cursor = 'grabbing';
  });

  canvas.addEventListener('mousemove', (e) => {
    if (!isDragging || capturedSamples.length === 0) return;

    const dx = e.clientX - dragStartX;
    const viewSamples = viewEnd - viewStart;
    const samplesPerPixel = viewSamples / canvas.width;
    const sampleDelta = -dx * samplesPerPixel;

    const newStart = dragStartViewStart + sampleDelta;
    const newEnd = newStart + viewSamples;

    setViewRange(newStart, newEnd);
  });

  canvas.addEventListener('mouseup', () => {
    isDragging = false;
    canvas.style.cursor = 'default';
  });

  canvas.addEventListener('mouseleave', () => {
    isDragging = false;
    canvas.style.cursor = 'default';
  });

  // Double-click to reset zoom
  canvas.addEventListener('dblclick', () => {
    resetView();
  });

  captureBtn?.addEventListener('click', () => {
    if (!synth.isInitialized()) return;

    if (!isCapturing) {
      // Start capture
      isCapturing = true;
      captureBtn.textContent = 'Stop';
      captureStatus.textContent = 'Recording... play a note!';
      captureStatus.style.color = '#f44';
      synth.startCapture();
    } else {
      // Stop capture and display
      isCapturing = false;
      captureBtn.textContent = 'Capture';
      captureStatus.textContent = 'Processing...';

      synth.stopCapture((samples, sampleRate) => {
        capturedSamples = samples;
        capturedSampleRate = sampleRate;
        captureStatus.textContent = `Captured ${samples.length} samples at ${sampleRate}Hz`;
        captureStatus.style.color = '#4f4';
        resetView();
        analyzeFrequency();
      });
    }
  });

  // Draw empty oscilloscope and FFT
  drawWaveform();
  analyzeFrequency();
}
